import { Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotification } from "~/hooks";
import { useCopyBooking, useGetBooking } from "~/services/booking";
import { capitalize } from "~/utils";
import { BookingList } from "./booking-list";
import { BookingPreview } from "./booking-preview";
import { CopyOptionsForm } from "./copy-options-form";
import { FormValues, TBookingPreview } from "./types";

export const CopyBooking = () => {
  const { id } = useParams();
  const { success } = useNotification();
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(
    null
  );
  const [expandedDetails, setExpandedDetails] = useState(true);

  useEffect(() => {
    if (id) {
      setSelectedBookingId(id);
    }
  }, [id]);

  const { data: bookingData, isLoading: isLoadingBooking } =
    useGetBooking(selectedBookingId);
  const mutation = useCopyBooking();

  const selectedBooking = useMemo<TBookingPreview | undefined>(() => {
    if (!bookingData) return;
    const { client, project, bookingDate, serviceTicket } = bookingData;
    const formattedServices = serviceTicket?.map((ticket) => ({
      type: capitalize(ticket.serviceType),
      location: "Artist Studio, Brooklyn",
    }));
    const formattedInventory =
      serviceTicket?.reduce<TBookingPreview["inventory"]>((acc, ticket) => {
        const inventoryItems =
          ticket.inventories?.map((item) => ({
            id: item.id,
            inventoryRef: item.inventoryRef ?? "",
            title: item.title || "",
            dimensions: `${item.width}" × ${item.height}" × ${item.length}"`,
          })) || [];
        return [...acc, ...inventoryItems];
      }, []) || [];
    return {
      id: bookingData.id,
      client:
        [client?.companyName || client?.firstName, client?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || "Unnamed Client",
      project: project?.name,
      date: dayjs(bookingDate).format("YYYY-MM-DD"),
      services: formattedServices,
      inventory: formattedInventory,
      referenceNumber: bookingData.referenceNumber,
    };
  }, [bookingData]);

  const onCopyBooking = (values: FormValues) => {
    if (!selectedBooking) return;
    mutation.mutate(
      {
        id: selectedBooking.id,
        copyClientInformation: values.copyClient,
        copyInventoryItems: values.copyInventory,
        copyServiceTypes: values.copyServices,
        copyDates: values.copyDates,
        newBookingDate: values.newBookingDate ?? undefined,
        newProjectName: values.newProjectName,
      },
      {
        onSuccess: () => {
          success({ message: "Booking copied successfully" });
        },
      }
    );
  };

  return (
    <Spin spinning={isLoadingBooking}>
      <div className="w-full bg-gray-50">
        <div className="bg-white rounded-lg border p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-medium mb-6">Copy Existing Booking</h2>
          <BookingList
            selectedBookingId={selectedBookingId}
            onSelectBooking={setSelectedBookingId}
          />
          {selectedBooking && (
            <BookingPreview
              booking={selectedBooking}
              expanded={expandedDetails}
              onToggleExpand={() => setExpandedDetails(!expandedDetails)}
            />
          )}
        </div>
        <CopyOptionsForm
          selectedBooking={selectedBooking}
          onSubmit={onCopyBooking}
          isSubmitting={mutation.isPending}
        />
      </div>
    </Spin>
  );
};
